// Sales: orders (an order opens in a sheet with its refunds), access codes and products, as tabs of one page.
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api, type Order } from '../api';
import { dollars, fmtDate, fmtDateTime, money, useAction, useLoad, usePaged, useTitle } from '../lib';
import { Alert, Button, Card, Empty, Facts, Field, Json, Loading, Page, Pager, Search, Sheet, TableCard } from '../ui';

export const statusBadge = (s: string) => <span className={`badge ${s === 'paid' ? 'badge-good' : s.includes('refunded') ? 'badge-warn' : ''}`}>{s.replace('_', ' ')}</span>;

const TABS = [{ to: '/sales/orders', label: 'Orders' }, { to: '/sales/codes', label: 'Codes' }, { to: '/sales/products', label: 'Products' }];
const STATUSES: [string, string][] = [['', 'All'], ['paid', 'Paid'], ['partially_refunded', 'Part refunded'], ['refunded', 'Refunded'], ['pending', 'Pending']];

interface OrderRow { id: string; receipt_no: number; email: string; account_id: string; status: string; total_cents: number; refunded_cents: number; created_at: string; paid_at: string | null }

export function Orders() {
  useTitle('Orders');
  const { id } = useParams();
  const [p, setP] = useSearchParams();
  const nav = useNavigate();
  const q = p.get('q') ?? '', status = p.get('status') ?? '';
  const set = useCallback((next: Record<string, string>) => setP(Object.fromEntries(Object.entries({ q, status, ...next }).filter(([, v]) => v))), [q, status, setP]);
  const list = usePaged<OrderRow>(`/admin/orders?q=${encodeURIComponent(q)}${status ? `&status=${status}` : ''}`, 'orders');
  return (
    <Page title="Sales" tabs={TABS}>
      <TableCard
        toolbar={<>
          <Search value={q} onSearch={(v) => set({ q: v })} placeholder="Email or receipt number" />
          <span className="seg" role="group" aria-label="Status">
            {STATUSES.map(([v, l]) => <button key={v} type="button" aria-pressed={status === v} onClick={() => set({ status: v })}>{l}</button>)}
          </span>
        </>}
        pager={list.rows && <Pager offset={list.offset} total={list.total} shown={list.rows.length} onPage={list.setOffset} />}
      >
        <Alert>{list.error}</Alert>
        {!list.rows ? <Loading /> : !list.rows.length ? <Empty>No orders match.</Empty> : (
          <table>
            <thead><tr><th>Receipt</th><th>Customer</th><th>Status</th><th className="num">Total</th><th className="num">Refunded</th><th>Paid</th></tr></thead>
            <tbody>
              {list.rows.map((o) => (
                <tr key={o.id} className={`click${o.id === id ? ' on' : ''}`} onClick={() => nav(`/sales/orders/${o.id}${location.search}`)}>
                  <td><Link to={`/sales/orders/${o.id}${location.search}`} onClick={(e) => e.stopPropagation()}>#{o.receipt_no}</Link></td>
                  <td>{o.email}</td>
                  <td>{statusBadge(o.status)}</td>
                  <td className="num">{dollars(o.total_cents)}</td>
                  <td className="num">{o.refunded_cents ? dollars(o.refunded_cents) : ''}</td>
                  <td className="nowrap muted">{o.paid_at ? fmtDateTime(o.paid_at) : `created ${fmtDate(o.created_at)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
      {id && <OrderSheet id={id} onClose={() => nav(`/sales/orders${location.search}`)} onChanged={() => void list.reload()} />}
    </Page>
  );
}

interface OrderDetail {
  order: Order & { email: string; accountId: string; stripePaymentIntent: string | null; billing: unknown };
  entitlements: { id: string; source: string; ends_at: string | null; revoked_at: string | null; order_item_id: string }[];
  refunds: { id: string; stripe_refund_id: string | null; amount_cents: number; actor: string; reason: string | null; created_at: string }[];
  unusedPasses: { itemId: string; cents: number; daysLeft: number }[];
}

function OrderSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data, error, reload } = useLoad(() => api.get<OrderDetail>(`/admin/orders/${id}`), [id]);
  const [msg, setMsg] = useState<string | null>(null);
  const resend = useAction(async () => { await api.post(`/admin/orders/${id}/resend-receipt`); setMsg(`Receipt sent to ${data?.order.email}.`); });
  const done = async (m: string) => { await reload(); onChanged(); setMsg(m); };
  if (error) return <Sheet title="Order" onClose={onClose}><Alert>{error}</Alert></Sheet>;
  if (!data) return <Sheet title="Order" onClose={onClose}><Loading /></Sheet>;
  const o = data.order;
  const refundable = ['paid', 'partially_refunded'].includes(o.status);
  return (
    <Sheet
      title={<>Receipt #{o.receiptNo}</>}
      sub={<>{statusBadge(o.status)}<Link to={`/customers/${o.accountId}`}>{o.email}</Link><span>{o.paidAt ? `Paid ${fmtDateTime(o.paidAt)}` : `Created ${fmtDateTime(o.createdAt)}`}</span></>}
      actions={o.paidAt ? <Button className="btn btn-sm" busy={resend.busy} onClick={() => void resend.run()}>Resend receipt</Button> : undefined}
      onClose={onClose}
    >
      <Alert kind="good">{msg}</Alert>
      <Alert>{resend.error}</Alert>
      <Card>
        <table className="table" style={{ marginTop: '1rem' }}>
          <thead><tr><th>Item</th><th className="num">Price</th><th className="num">Tax</th><th className="num">Refunded</th></tr></thead>
          <tbody>
            {o.items.map((i) => (
              <tr key={i.id}>
                <td>{i.description}{i.discountCents ? <div className="small muted">discount −{dollars(i.discountCents)}</div> : null}</td>
                <td className="num">{dollars(i.amountCents)}</td>
                <td className="num">{i.taxCents ? dollars(i.taxCents) : ''}</td>
                <td className="num">{i.refundedCents ? dollars(i.refundedCents) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><th>Total</th><th className="num">{dollars(o.totalCents)}</th><th /><th className="num">{o.refundedCents ? dollars(o.refundedCents) : ''}</th></tr></tfoot>
        </table>
        <div style={{ marginTop: '.75rem' }}>
          <Facts items={[
            ['Stripe payment', o.stripePaymentIntent ? <span className="mono">{o.stripePaymentIntent}</span> : o.paidAt ? 'Development payment' : ''],
            ['Plan time', data.entitlements.map((e) => `${e.ends_at ? `until ${fmtDate(e.ends_at)}` : 'lifetime'}${e.revoked_at ? ' (revoked)' : ''}`).join(', ') || 'None'],
          ]} />
          <Json value={o.billing} />
        </div>
      </Card>
      {data.refunds.length > 0 && (
        <Card title="Refunds">
          <table className="table">
            <tbody>
              {data.refunds.map((r) => (
                <tr key={r.id}><td className="nowrap">{fmtDateTime(r.created_at)}</td><td className="num">{dollars(r.amount_cents)}</td><td>{r.reason}<div className="small muted">{r.actor} {r.stripe_refund_id && <span className="mono">{r.stripe_refund_id}</span>}</div></td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {refundable && data.unusedPasses.length > 0 && <UnusedPlan order={o} lines={data.unusedPasses} onDone={done} />}
      {refundable && <Refund order={o} onDone={() => done('Refund recorded.')} />}
    </Sheet>
  );
}

function Refund({ order, onDone }: { order: Order; onDone: () => Promise<void> }) {
  const open = order.items.filter((i) => i.refundedCents < i.amountCents + i.taxCents);
  const [items, setItems] = useState<string[]>([]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const lineTotal = order.items.filter((i) => items.includes(i.id)).reduce((s, i) => s + i.amountCents + i.taxCents - i.refundedCents, 0);
  const cents = amount ? Math.round(Number(amount) * 100) : lineTotal;
  const run = useAction(async () => {
    if (!confirm(`Refund ${dollars(cents)}${items.length ? ' and end the plan time from the ticked lines' : ''}? This sends the money back through Stripe.`)) return;
    await api.post(`/admin/orders/${order.id}/refund`, { itemIds: items, amountCents: amount ? cents : null, reason });
    setItems([]); setAmount(''); setReason('');
    await onDone();
  });
  return (
    <Card title="Refund" sub={`${dollars(order.totalCents - order.refundedCents)} left on this order`}>
      <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); void run.run(); }}>
        <p className="small muted" style={{ margin: 0 }}>Tick a line to refund it in full and end its plan time, or enter an amount for a partial refund that keeps the plan.</p>
        {open.map((i) => (
          <label key={i.id} className="check">
            <input type="checkbox" checked={items.includes(i.id)} onChange={(e) => setItems(e.target.checked ? [...items, i.id] : items.filter((x) => x !== i.id))} />
            {i.description} ({dollars(i.amountCents + i.taxCents - i.refundedCents)})
          </label>
        ))}
        <div className="form-row" style={{ gridTemplateColumns: '9rem 1fr' }}>
          <Field label="Amount (USD)" type="number" min="0.01" step="0.01" placeholder={lineTotal ? (lineTotal / 100).toFixed(2) : ''} value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Field label="Reason" required maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <Alert>{run.error}</Alert>
        <Button className="btn btn-danger" busy={run.busy} disabled={!cents || !reason}>Refund {cents ? dollars(cents) : ''}</Button>
      </form>
    </Card>
  );
}

/** The pro-rata refund the terms promise when an account is closed for a reason other than a breach, or if
 *  Math Ladder shuts down: refunds the unused share of a yearly plan and ends it. */
function UnusedPlan({ order, lines, onDone }: { order: Order; lines: { itemId: string; cents: number; daysLeft: number }[]; onDone: (msg: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const run = useAction(async (itemId: string, cents: number) => {
    if (!confirm(`Refund ${dollars(cents)} for the unused plan time and end the plan now? This sends the money back through Stripe and emails the customer.`)) return;
    await api.post(`/admin/orders/${order.id}/refund-unused-pass`, { itemId, reason });
    setReason('');
    await onDone(`Refunded ${dollars(cents)} of unused plan time.`);
  });
  return (
    <Card title="Refund unused plan time" sub="Closing an account for a reason other than a breach, or shutting down (terms §8 and §10)">
      <div className="stack-sm">
        <Field label="Reason" required maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
        {lines.map((l) => (
          <div key={l.itemId} className="row-between">
            <span className="small">{order.items.find((i) => i.id === l.itemId)?.description} · {l.daysLeft} days left</span>
            <Button className="btn btn-danger btn-sm" busy={run.busy} disabled={reason.trim().length < 3} onClick={() => void run.run(l.itemId, l.cents)}>Refund {dollars(l.cents)} and end it</Button>
          </div>
        ))}
        <Alert>{run.error}</Alert>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- access codes
interface Code { code: string; stages: number[]; lifetime: boolean; pass_days: number | null; max_redemptions: number; redemptions: number; expires_at: string | null; note: string | null; created_at: string; disabled_at: string | null; sent_to: string | null; sent_at: string | null }

const redeemLink = (c: string) => `${location.hostname === 'localhost' ? 'http://localhost:5173' : 'https://mathladder.net'}/app/redeem?code=${c}`;
const codeState = (c: Code) => (c.disabled_at ? 'disabled' : c.expires_at && new Date(c.expires_at) < new Date() ? 'expired' : c.redemptions >= c.max_redemptions ? 'used' : 'open');

export function Codes() {
  useTitle('Codes');
  const { data, error, reload } = useLoad(() => api.get<{ codes: Code[] }>('/admin/codes'));
  const [making, setMaking] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const disable = useAction(async (code: string) => { if (confirm(`Disable ${code}?`)) { await api.post(`/admin/codes/${encodeURIComponent(code)}/disable`); await reload(); } });
  const open = data?.codes.filter((c) => codeState(c) === 'open').length ?? 0;
  return (
    <Page title="Sales" tabs={TABS} actions={<button className="btn btn-primary btn-sm" onClick={() => setMaking(true)}><Plus size={15} /> New codes</button>}>
      <Alert kind="good">{msg}</Alert>
      <TableCard toolbar={<span className="small muted">{data ? `${data.codes.length} codes · ${open} still usable` : ''}</span>}>
        <Alert>{error || disable.error}</Alert>
        {!data ? <Loading /> : !data.codes.length ? <Empty>No codes yet.</Empty> : (
          <table>
            <thead><tr><th>Code</th><th>Gives</th><th className="num">Used</th><th>Expires</th><th>For</th><th>Emailed to</th><th>Made</th><th /></tr></thead>
            <tbody>
              {data.codes.map((c) => {
                const st = codeState(c);
                return (
                  <tr key={c.code} style={st !== 'open' ? { opacity: 0.55 } : undefined}>
                    <td className="mono nowrap">{c.code}</td>
                    <td className="nowrap">{c.lifetime ? 'Lifetime' : c.pass_days ? `${c.pass_days} days` : `Old: units ${c.stages.join(', ')}`}</td>
                    <td className="num">{c.redemptions}/{c.max_redemptions}</td>
                    <td className="nowrap">{c.expires_at ? fmtDate(c.expires_at) : ''}</td>
                    <td className="small">{c.note}</td>
                    <td className="small">{c.sent_to}{c.sent_at && <div className="muted">{fmtDate(c.sent_at)}</div>}</td>
                    <td className="nowrap muted">{fmtDate(c.created_at)}</td>
                    <td className="nowrap">
                      {st === 'open' ? <>
                        <button className="btn btn-quiet btn-sm" onClick={() => setSending(c.code)}>Email</button>
                        <button className="btn btn-quiet btn-sm" onClick={() => void disable.run(c.code)}>Disable</button>
                      </> : <span className="badge">{st}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </TableCard>
      {making && <MakeCodes onClose={() => setMaking(false)} onMade={() => void reload()} />}
      {sending && <SendCode code={sending} onClose={() => setSending(null)} onSent={async (to) => { setSending(null); setMsg(`${sending} was emailed to ${to}.`); await reload(); }} />}
    </Page>
  );
}

function Message({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="code-msg">Personal message (optional)</label>
      <textarea id="code-msg" className="input" rows={3} maxLength={1000} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Hi Sarah, here is a year of Math Ladder. Enjoy!" />
      <div className="hint">Shown above the code. It comes from support@mathladder.net, so replies reach you.</div>
    </div>
  );
}

function MakeCodes({ onClose, onMade }: { onClose: () => void; onMade: () => void }) {
  const [f, setF] = useState({ lifetime: false, passDays: '365', maxRedemptions: '1', expiresAt: '', note: '', count: '1', sendTo: '', message: '' });
  const [made, setMade] = useState<{ codes: string[]; sentTo: string | null } | null>(null);
  const create = useAction(async () => {
    const r = await api.post<{ codes: string[]; sentTo: string | null }>('/admin/codes', {
      lifetime: f.lifetime, passDays: f.lifetime || !f.passDays ? null : Number(f.passDays), maxRedemptions: Number(f.maxRedemptions), count: Number(f.count),
      expiresAt: f.expiresAt ? new Date(`${f.expiresAt}T23:59:59`).toISOString() : null, note: f.note || null,
      sendTo: f.sendTo.trim() || null, message: f.message.trim() || null,
    });
    setMade(r);
    onMade();
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <Sheet title="New codes" sub="A code gives an account Unlimited for some days (after any time it has), or for life. Each account can use a code once." onClose={onClose}>
      {made ? (
        <Card>
          <div style={{ paddingTop: '1rem' }} className="stack-sm">
            <Alert kind="good">{made.sentTo ? `Made and emailed to ${made.sentTo}.` : `${made.codes.length === 1 ? 'Code' : 'Codes'} made. The link opens the redeem page with the code filled in.`}</Alert>
            <pre className="json" style={{ maxWidth: 'none' }}>{made.codes.map((c) => `${c}   ${redeemLink(c)}`).join('\n')}</pre>
            <div className="row"><button className="btn" onClick={() => setMade(null)}>Make more</button><button className="btn btn-quiet" onClick={onClose}>Done</button></div>
          </div>
        </Card>
      ) : (
        <Card>
          <form className="stack-sm" style={{ paddingTop: '1rem' }} onSubmit={(e) => { e.preventDefault(); void create.run(); }}>
            <label className="check"><input type="checkbox" checked={f.lifetime} onChange={set('lifetime')} /> Lifetime</label>
            <div className="form-row">
              {!f.lifetime && <Field label="Days" type="number" min={1} max={3650} value={f.passDays} onChange={set('passDays')} />}
              <Field label="Uses per code" type="number" min={1} max={10000} value={f.maxRedemptions} onChange={set('maxRedemptions')} />
              <Field label="How many codes" type="number" min={1} max={200} value={f.count} onChange={set('count')} />
              <Field label="Expires" type="date" value={f.expiresAt} onChange={set('expiresAt')} />
            </div>
            <Field label="For (only admins see this)" maxLength={300} value={f.note} onChange={set('note')} placeholder="Sarah's co-op" />
            {f.count === '1' && <>
              <Field label="Email it to (optional)" type="email" value={f.sendTo} onChange={set('sendTo')} placeholder="parent@example.com" />
              {f.sendTo.trim() && <Message value={f.message} onChange={(v) => setF({ ...f, message: v })} />}
            </>}
            <Alert>{create.error}</Alert>
            <Button className="btn btn-primary" busy={create.busy}>{f.count === '1' && f.sendTo.trim() ? 'Make and email the code' : f.count === '1' ? 'Make the code' : `Make ${f.count} codes`}</Button>
          </form>
        </Card>
      )}
    </Sheet>
  );
}

function SendCode({ code, onClose, onSent }: { code: string; onClose: () => void; onSent: (to: string) => Promise<void> }) {
  const [mail, setMail] = useState({ to: '', message: '' });
  const send = useAction(async () => {
    await api.post(`/admin/codes/${encodeURIComponent(code)}/send`, { to: mail.to.trim(), message: mail.message.trim() || null });
    await onSent(mail.to.trim());
  });
  return (
    <Sheet title={`Email ${code}`} onClose={onClose}>
      <Card>
        <form className="stack-sm" style={{ paddingTop: '1rem' }} onSubmit={(e) => { e.preventDefault(); void send.run(); }}>
          <Field label="To" type="email" required autoFocus value={mail.to} onChange={(e) => setMail({ ...mail, to: e.target.value })} placeholder="parent@example.com" />
          <Message value={mail.message} onChange={(v) => setMail({ ...mail, message: v })} />
          <Alert>{send.error}</Alert>
          <Button className="btn btn-primary" busy={send.busy}>Send</Button>
        </form>
      </Card>
    </Sheet>
  );
}

// ---------------------------------------------------------------- products
interface ProductRow { code: string; kind: string; name: string; description: string; stages: number[]; pass_days: number | null; price_cents: number; active: boolean }

export function Products() {
  useTitle('Products');
  const p = useLoad(() => api.get<{ products: ProductRow[] }>('/admin/products'));
  const plans = p.data?.products.filter((x) => x.kind === 'plan') ?? [];
  const old = p.data?.products.filter((x) => x.kind !== 'plan') ?? [];
  return (
    <Page title="Sales" tabs={TABS}>
      <Alert>{p.error}</Alert>
      {!p.data ? <Loading /> : (
        <>
          <div className="split2" style={{ flex: 'none' }}>{plans.map((x) => <ProductForm key={x.code} p={x} />)}</div>
          <p className="small muted" style={{ margin: 0 }}>A price change applies to new checkouts; paid orders keep their price. {old.length} products from the old per-unit shop are retired and not for sale.</p>
        </>
      )}
    </Page>
  );
}

function ProductForm({ p }: { p: ProductRow }) {
  const [f, setF] = useState({ name: p.name, description: p.description, price: (p.price_cents / 100).toFixed(2), active: p.active });
  const [ok, setOk] = useState(false);
  const save = useAction(async () => {
    await api.patch(`/admin/products/${p.code}`, { name: f.name, description: f.description, priceCents: Math.round(Number(f.price) * 100), active: f.active });
    setOk(true);
  });
  const set = (k: 'name' | 'description' | 'price') => (e: React.ChangeEvent<HTMLInputElement>) => { setOk(false); setF({ ...f, [k]: e.target.value }); };
  return (
    <Card title={<>{p.name} <span className="mono muted">{p.code}</span></>} sub={`${p.pass_days ? `${p.pass_days} days` : 'Lifetime'} · now ${money(p.price_cents)}${p.active ? '' : ' · not for sale'}`}>
      <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); void save.run(); }}>
        <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
          <Field label="Name" required value={f.name} onChange={set('name')} />
          <Field label="Price (USD)" type="number" min="0.5" step="0.01" required value={f.price} onChange={set('price')} />
        </div>
        <Field label="Description" value={f.description} onChange={set('description')} />
        <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => { setOk(false); setF({ ...f, active: e.target.checked }); }} /> For sale</label>
        <Alert>{save.error}</Alert>
        <div className="row"><Button className="btn btn-sm btn-primary" busy={save.busy}>Save</Button>{ok && <span className="small" style={{ color: 'var(--good)' }}>Saved.</span>}</div>
      </form>
    </Card>
  );
}
